/**
 * Transak Onramp Widget — custom first-screen controller.
 *
 * Responsibilities:
 *  - Render fiat / crypto / payment-method dropdowns from server-injected lookups.
 *  - Filter payment methods by the selected fiat (matches the real widget).
 *  - Auto-select the default network for each crypto (no network picker).
 *  - Call /transak-onramp/quote with debounce on amount/currency change,
 *    plus a visibility-aware auto-refresh every 30 s while the screen is visible.
 *  - On "Buy now" / "Sell now": POST to /transak-onramp/session, then embed the
 *    real Transak widget with hideExchangeScreen=true so the user lands directly
 *    on the wallet-address screen.
 *
 * Fully separate from the legacy [data-transak-widget] controller. Different
 * selector, different file, different endpoints — touching one cannot affect the other.
 *
 * @see template-parts/blocks/transak-onramp/transak-onramp.php
 * @see inc/transak-onramp-api.php
 */

const QUOTE_DEBOUNCE_MS = 350;

/**
 * Stuck-widget watchdog window: if TRANSAK_WIDGET_INITIALISED hasn't fired within
 * this time after the SDK receives the widgetUrl, emit `transak_onramp_widget_stuck`
 * (Transak refusing to serve / silent iframe failure) and hide the spinner. Kept
 * below the previous 15s spinner-hide so we signal before the spinner disappears.
 */
const STUCK_TIMEOUT_MS = 12000;

const CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="block w-5 h-5"><path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM11.0026 16L18.0737 8.92893L16.6595 7.51472L11.0026 13.1716L8.17421 10.3431L6.75999 11.7574L11.0026 16Z" fill="currentColor"/></svg>';
const CLOCK_SVG = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="block shrink-0"><path d="M17.6177 5.9681L19.0711 4.51472L20.4853 5.92893L19.0319 7.38231C20.2635 8.92199 21 10.875 21 13C21 17.9706 16.9706 22 12 22C7.02944 22 3 17.9706 3 13C3 8.02944 7.02944 4 12 4C14.125 4 16.078 4.73647 17.6177 5.9681ZM12 20C15.866 20 19 16.866 19 13C19 9.13401 15.866 6 12 6C8.13401 6 5 9.13401 5 13C5 16.866 8.13401 20 12 20ZM11 8H13V14H11V8ZM8 1H16V3H8V1Z"/></svg>';

/** Decimal formatter that drops trailing zeros for crypto, keeps 2 dp for fiat. */
function formatAmount(value, isCrypto) {
    if (value === null || value === undefined || isNaN(value)) return '';
    if (isCrypto) {
        return parseFloat(value.toFixed(8)).toString();
    }
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export default class TransakOnrampWidget {

    /**
     * @param {HTMLElement} el - The `[data-transak-onramp-widget]` root.
     */
    constructor(el) {
        this.el = el;

        // Lookups dataset is inlined as JSON next to the card. If missing (cron
        // not yet populated and synchronous refresh failed), bail — the block
        // still renders the static markup, just without interactive dropdowns.
        const lookupsScript = el.querySelector('script[data-onramp-lookups]');
        if (!lookupsScript) return;

        try {
            this.lookups = JSON.parse(lookupsScript.textContent);
        } catch (err) {
            console.error('[TransakOnramp] invalid lookups JSON', err);
            return;
        }

        if (!this.lookups?.fiats?.length || !this.lookups?.cryptos?.length) return;

        // Endpoints + initial config.
        this.quoteEndpoint   = el.dataset.quoteEndpoint;
        this.sessionEndpoint = el.dataset.sessionEndpoint;
        this.product         = (el.dataset.defaultProduct || 'BUY').toUpperCase();

        // Initial selection — pick from the lookups dataset matching what the
        // template SSR'd, so the JS boot mirrors the visible state exactly.
        const initialFiat   = el.querySelector('[data-onramp-fiat-code]')?.textContent?.trim();
        const initialCrypto = el.querySelector('[data-onramp-crypto-code]')?.textContent?.trim();

        this.state = {
            fiat:    this.lookups.fiats.find(f => f.code === initialFiat) || this.lookups.fiats[0],
            crypto:  this.lookups.cryptos.find(c => c.code === initialCrypto) || this.lookups.cryptos[0],
            payment: null,
            fiatAmount:   parseFloat(el.dataset.defaultFiatAmount) || 100,
            cryptoAmount: null,
            quoteId: null,
        };
        this.state.payment = this._defaultPaymentForFiat(this.state.fiat);

        // DOM refs — flat for easy access; null-safe via optional chaining in callers.
        this.refs = {
            title:        el.querySelector('[data-onramp-title]'),
            sourceInput:  el.querySelector('[data-onramp-source-input]'),
            destInput:    el.querySelector('[data-onramp-dest-input]'),
            fiatIcon:     el.querySelector('[data-onramp-fiat-icon]'),
            fiatCode:     el.querySelector('[data-onramp-fiat-code]'),
            cryptoIcon:   el.querySelector('[data-onramp-crypto-icon]'),
            cryptoCode:   el.querySelector('[data-onramp-crypto-code]'),
            networkIcon:  el.querySelector('[data-onramp-network-icon]'),
            network:      el.querySelector('[data-onramp-network]'),
            paymentIcon:  el.querySelector('[data-onramp-payment-icon]'),
            paymentName:  el.querySelector('[data-onramp-payment-name]'),
            paymentTime:  el.querySelector('[data-onramp-payment-time]'),
            rate:         el.querySelector('[data-onramp-rate]'),
            error:        el.querySelector('[data-onramp-error]'),
            cta:          el.querySelector('[data-onramp-cta]'),
            ctaLabel:     el.querySelector('[data-onramp-cta-label]'),
            overlay:      el.querySelector('[data-onramp-overlay]'),
            panels: {
                fiat:    el.querySelector('[data-onramp-panel="fiat"]'),
                crypto:  el.querySelector('[data-onramp-panel="crypto"]'),
                payment: el.querySelector('[data-onramp-panel="payment"]'),
            },
            lists: {
                fiat:           el.querySelector('[data-onramp-list="fiat"]'),
                cryptoPopular:  el.querySelector('[data-onramp-list="crypto-popular"]'),
                cryptoAll:      el.querySelector('[data-onramp-list="crypto-all"]'),
                payment:        el.querySelector('[data-onramp-list="payment"]'),
            },
            embed:        el.querySelector('[data-onramp-embed-container]'),
            embedLoading: el.querySelector('[data-onramp-loading]'),
            screen:       el.querySelector('[data-onramp-screen]'),
        };

        this._quoteAbort = null;
        this._quoteTimer = null;
        this._activePanel = null;
        this._embedded = false;
        this._transak = null;
        this._stuckTimer = null;
        this.buyCountry = el.dataset.buyCountry || 'XX';

        this._render();
        this._bindEvents();
        this._scheduleQuote(0);  // fetch initial quote immediately
    }

    // -------------------------------------------------------------------------
    // Selection helpers
    // -------------------------------------------------------------------------

    _defaultPaymentForFiat(fiat) {
        if (!fiat?.paymentOptions?.length) return null;
        // Prefer a card-style method when available; otherwise first.
        return fiat.paymentOptions.find(p => /card/i.test(p.id)) || fiat.paymentOptions[0];
    }

    _setFiat(code) {
        const next = this.lookups.fiats.find(f => f.code === code);
        if (!next || next.code === this.state.fiat.code) {
            this._closePanel();
            return;
        }
        this.state.fiat = next;
        // Payment methods are per-fiat — re-pick a sensible default whenever fiat changes.
        this.state.payment = this._defaultPaymentForFiat(next);
        this._render();
        this._closePanel();
        this._scheduleQuote(0);
    }

    _setCrypto(code) {
        const next = this.lookups.cryptos.find(c => c.code === code);
        if (!next || next.code === this.state.crypto.code) {
            this._closePanel();
            return;
        }
        this.state.crypto = next;
        this._render();
        this._closePanel();
        this._scheduleQuote(0);
    }

    _setPayment(id) {
        const next = this.state.fiat.paymentOptions.find(p => p.id === id);
        if (!next || next.id === this.state.payment?.id) {
            this._closePanel();
            return;
        }
        this.state.payment = next;
        this._render();
        this._closePanel();
        this._scheduleQuote(0);
    }

    _setProduct(product) {
        if (product !== 'BUY' && product !== 'SELL') return;
        this.product = product;
        this._render();
        this._scheduleQuote(0);
    }

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    _render() {
        const { fiat, crypto, payment } = this.state;

        if (this.refs.fiatIcon)    this.refs.fiatIcon.src = fiat.icon || '';
        if (this.refs.fiatCode)    this.refs.fiatCode.textContent = fiat.code;
        if (this.refs.cryptoIcon)  this.refs.cryptoIcon.src = crypto.icon || '';
        if (this.refs.cryptoCode)  this.refs.cryptoCode.textContent = crypto.code;
        if (this.refs.networkIcon) this.refs.networkIcon.src = crypto.icon || '';
        if (this.refs.network)     this.refs.network.textContent = `${crypto.network.charAt(0).toUpperCase() + crypto.network.slice(1)} Network`;

        if (payment) {
            if (this.refs.paymentIcon) this.refs.paymentIcon.src = payment.icon || '';
            if (this.refs.paymentName) this.refs.paymentName.textContent = payment.name;
            if (this.refs.paymentTime) this.refs.paymentTime.textContent = payment.time;
        }

        if (this.refs.ctaLabel) {
            this.refs.ctaLabel.textContent = this.product === 'SELL' ? 'Sell now' : 'Buy now';
        }
        if (this.refs.title) {
            this.refs.title.textContent = this.product === 'SELL'
                ? 'Sell crypto from your wallet'
                : 'Buy crypto to your wallet';
        }
    }

    _renderFiatList(filter = '') {
        const list = this.refs.lists.fiat;
        if (!list) return;
        const filtered = this.lookups.fiats.filter(f =>
            !filter || f.code.toLowerCase().includes(filter) || f.name.toLowerCase().includes(filter)
        );
        list.innerHTML = filtered.map(f => this._fiatLi(f)).join('');
    }

    _renderCryptoLists(filter = '') {
        const popList = this.refs.lists.cryptoPopular;
        const allList = this.refs.lists.cryptoAll;
        if (!popList || !allList) return;

        const filterFn = c =>
            !filter || c.code.toLowerCase().includes(filter) || c.name.toLowerCase().includes(filter);

        popList.innerHTML = this.lookups.cryptos.filter(c => c.popular).filter(filterFn).map(c => this._cryptoLi(c)).join('');
        allList.innerHTML = this.lookups.cryptos.filter(c => !c.popular).filter(filterFn).map(c => this._cryptoLi(c)).join('');
    }

    _renderPaymentList() {
        const list = this.refs.lists.payment;
        if (!list) return;
        const methods = this.state.fiat.paymentOptions || [];
        const selectedId = this.state.payment?.id;
        list.innerHTML = methods.map(m => `
            <div class="flex items-center gap-3 p-4 rounded-[10px] cursor-pointer transition-colors hover:bg-grey-50 ${m.id === selectedId ? 'bg-grey-50' : ''}" data-pm="${escapeHtml(m.id)}">
                <span class="w-10 h-10 shrink-0 block">
                    <img src="${escapeHtml(m.icon)}" alt="" class="w-10 h-10 block" loading="lazy" width="40" height="40">
                </span>
                <span class="flex flex-col gap-0.5 flex-1">
                    <span class="text-base font-semibold text-grey-900 leading-[22px]">${escapeHtml(m.name)}</span>
                    <span class="flex items-center gap-1 text-grey-600">
                        ${CLOCK_SVG}
                        <span class="text-sm font-semibold text-grey-600 leading-5">${escapeHtml(m.time)}</span>
                    </span>
                </span>
                ${m.id === selectedId ? `<span class="w-5 h-5 shrink-0 text-[#5B4BE7]">${CHECK_SVG}</span>` : ''}
            </div>
        `).join('');
    }

    _fiatLi(f) {
        const selected = f.code === this.state.fiat.code;
        return `
            <li class="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors hover:bg-grey-50 ${selected ? 'bg-grey-50' : ''}" data-fiat="${escapeHtml(f.code)}">
                <img src="${escapeHtml(f.icon)}" alt="" class="w-10 h-10 rounded-[10px] object-cover shrink-0" loading="lazy" width="40" height="40">
                <div class="flex flex-col gap-0.5 flex-1 min-w-0">
                    <strong class="text-sm font-bold text-grey-900 leading-[21px]">${escapeHtml(f.code)}</strong>
                    <span class="text-xs font-medium text-grey-600 leading-[18px]">${escapeHtml(f.name)}</span>
                </div>
                ${selected ? `<span class="w-5 h-5 shrink-0 text-[#5B4BE7]">${CHECK_SVG}</span>` : ''}
            </li>
        `;
    }

    _cryptoLi(c) {
        const selected = c.code === this.state.crypto.code;
        return `
            <li class="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors hover:bg-grey-50 ${selected ? 'bg-grey-50' : ''}" data-crypto="${escapeHtml(c.code)}">
                <img src="${escapeHtml(c.icon)}" alt="" class="w-8 h-8 rounded-full object-cover shrink-0" loading="lazy" width="32" height="32">
                <div class="flex flex-col gap-0.5 flex-1 min-w-0">
                    <strong class="text-sm font-bold text-grey-900 leading-[21px]">${escapeHtml(c.code)}</strong>
                    <span class="text-xs font-medium text-grey-600 leading-[18px]">${escapeHtml(c.name)} on ${escapeHtml(c.network)}</span>
                </div>
                ${selected ? `<span class="w-5 h-5 shrink-0 text-[#5B4BE7]">${CHECK_SVG}</span>` : ''}
            </li>
        `;
    }

    // -------------------------------------------------------------------------
    // Events
    // -------------------------------------------------------------------------

    _bindEvents() {
        // Open dropdowns.
        this.el.querySelectorAll('[data-onramp-dropdown]').forEach(btn => {
            btn.addEventListener('click', () => this._openPanel(btn.dataset.onrampDropdown));
        });

        // Close buttons + overlay click.
        this.el.querySelectorAll('[data-onramp-close]').forEach(btn => {
            btn.addEventListener('click', () => this._closePanel());
        });
        this.refs.overlay?.addEventListener('click', () => this._closePanel());

        // ESC closes.
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this._activePanel) this._closePanel();
        });

        // List item clicks — delegated.
        this.refs.lists.fiat?.addEventListener('click', (e) => {
            const li = e.target.closest('[data-fiat]');
            if (li) this._setFiat(li.dataset.fiat);
        });
        this.refs.lists.cryptoPopular?.addEventListener('click', (e) => {
            const li = e.target.closest('[data-crypto]');
            if (li) this._setCrypto(li.dataset.crypto);
        });
        this.refs.lists.cryptoAll?.addEventListener('click', (e) => {
            const li = e.target.closest('[data-crypto]');
            if (li) this._setCrypto(li.dataset.crypto);
        });
        this.refs.lists.payment?.addEventListener('click', (e) => {
            const item = e.target.closest('[data-pm]');
            if (item) this._setPayment(item.dataset.pm);
        });

        // Search filtering.
        this.el.querySelector('[data-onramp-search="fiat"]')?.addEventListener('input', (e) => {
            this._renderFiatList(e.target.value.toLowerCase());
        });
        this.el.querySelector('[data-onramp-search="crypto"]')?.addEventListener('input', (e) => {
            this._renderCryptoLists(e.target.value.toLowerCase());
        });

        // Amount input — debounced quote.
        this.refs.sourceInput?.addEventListener('input', () => {
            const v = parseFloat(this.refs.sourceInput.value.replace(/[^0-9.]/g, ''));
            if (this.product === 'BUY') {
                this.state.fiatAmount = isNaN(v) ? null : v;
            } else {
                this.state.cryptoAmount = isNaN(v) ? null : v;
            }
            this._scheduleQuote(QUOTE_DEBOUNCE_MS);
        });

        // CTA — handoff to Transak widget.
        this.refs.cta?.addEventListener('click', () => this._handoff());
    }

    // -------------------------------------------------------------------------
    // Panel open/close
    // -------------------------------------------------------------------------

    _openPanel(type) {
        if (!this.refs.panels[type]) return;
        if (type === 'fiat')    this._renderFiatList();
        if (type === 'crypto')  this._renderCryptoLists();
        if (type === 'payment') this._renderPaymentList();

        // Clear search inputs each open so users see the full list.
        const searchInput = this.el.querySelector(`[data-onramp-search="${type}"]`);
        if (searchInput) searchInput.value = '';

        this._activePanel = type;
        this.refs.overlay?.classList.remove('opacity-0', 'pointer-events-none');
        this.refs.overlay?.classList.add('opacity-100');
        const panel = this.refs.panels[type];
        panel.classList.remove('translate-y-full');
        panel.classList.add('translate-y-0');

        // Focus search after slide animation.
        if (searchInput) setTimeout(() => searchInput.focus(), 350);
    }

    _closePanel() {
        if (!this._activePanel) return;
        this.refs.overlay?.classList.add('opacity-0', 'pointer-events-none');
        this.refs.overlay?.classList.remove('opacity-100');
        Object.values(this.refs.panels).forEach(p => {
            p?.classList.add('translate-y-full');
            p?.classList.remove('translate-y-0');
        });
        this._activePanel = null;
    }

    // -------------------------------------------------------------------------
    // Quote fetching
    // -------------------------------------------------------------------------

    _scheduleQuote(delayMs) {
        clearTimeout(this._quoteTimer);
        this._quoteTimer = setTimeout(() => this._fetchQuote(), delayMs);
    }

    async _fetchQuote() {
        const { fiat, crypto, payment, fiatAmount, cryptoAmount } = this.state;
        if (!payment) return;

        // Require a positive amount on the active side.
        const buy = this.product === 'BUY';
        if (buy && (!fiatAmount || fiatAmount <= 0)) {
            this._hideError();
            this.refs.destInput.value = '';
            this.refs.rate.textContent = '';
            return;
        }
        if (!buy && (!cryptoAmount || cryptoAmount <= 0)) {
            this._hideError();
            this.refs.destInput.value = '';
            this.refs.rate.textContent = '';
            return;
        }

        this._quoteAbort?.abort();
        this._quoteAbort = new AbortController();

        const body = {
            fiatCurrency:   fiat.code,
            cryptoCurrency: crypto.code,
            network:        crypto.network,
            paymentMethod:  payment.id,
            isBuyOrSell:    this.product,
        };
        if (buy) body.fiatAmount = String(fiatAmount);
        else     body.cryptoAmount = String(cryptoAmount);

        try {
            const resp = await fetch(this.quoteEndpoint, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                cache:   'no-store',
                body:    JSON.stringify(body),
                signal:  this._quoteAbort.signal,
            });

            if (!resp.ok) {
                let msg = `Quote unavailable (HTTP ${resp.status})`;
                try { const j = await resp.json(); if (j?.message) msg = j.message; } catch (_) {}
                this._showError(msg);
                this.refs.destInput.value = '';
                return;
            }

            const q = await resp.json();
            this.state.quoteId = q.quoteId || null;
            this._hideError();

            // Update the *opposite* field with the live amount Transak returned.
            if (buy) {
                this.refs.destInput.value = formatAmount(q.cryptoAmount, true);
            } else {
                this.refs.destInput.value = formatAmount(q.fiatAmount, false);
            }

            if (q.conversionPrice) {
                const fmt = formatAmount(q.conversionPrice, false);
                this.refs.rate.textContent = `1 ${crypto.code} ≈ ${fmt} ${fiat.code}`;
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('[TransakOnramp] quote error', err);
            this._showError('Could not refresh price. Please try again.');
        }
    }

    // -------------------------------------------------------------------------
    // Handoff: POST /session → embed Transak widget
    // -------------------------------------------------------------------------

    async _handoff() {
        if (this._embedded) return;

        const { fiat, crypto, payment, fiatAmount, cryptoAmount } = this.state;
        if (!payment) return;

        const buy = this.product === 'BUY';
        if (buy && (!fiatAmount || fiatAmount <= 0)) {
            this._showError('Enter an amount to continue.');
            return;
        }
        if (!buy && (!cryptoAmount || cryptoAmount <= 0)) {
            this._showError('Enter an amount to continue.');
            return;
        }

        this.refs.cta.disabled = true;
        this._hideError();

        try {
            // Kick off SDK download in parallel with the session request — same trick
            // the legacy widget uses to overlap network time with the import chunk.
            const sdkPromise = import('@transak/ui-js-sdk');

            const body = {
                fiatCurrency:       fiat.code,
                cryptoCurrencyCode: crypto.code,
                network:            crypto.network,
                paymentMethod:      payment.id,
                productsAvailed:    this.product,
            };
            if (buy) body.fiatAmount = String(fiatAmount);
            else     body.cryptoAmount = String(cryptoAmount);

            const resp = await fetch(this.sessionEndpoint, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                cache:   'no-store',
                body:    JSON.stringify(body),
            });

            if (!resp.ok) {
                let msg = `Could not start checkout (HTTP ${resp.status})`;
                try { const j = await resp.json(); if (j?.message) msg = j.message; } catch (_) {}
                throw new Error(msg);
            }

            const { widgetUrl } = await resp.json();
            if (!widgetUrl) throw new Error('Server response missing widgetUrl.');

            const { Transak } = await sdkPromise;

            this._embedded = true;
            this.refs.screen.classList.add('hidden');
            this.refs.embed.classList.remove('hidden');
            this.refs.embedLoading.classList.remove('hidden');
            this.refs.embedLoading.classList.add('flex');

            this._transak = new Transak({
                widgetUrl,
                containerId: this.refs.embed.id,
            });

            const loadStart = (window.performance && performance.now) ? performance.now() : null;
            this._startStuckWatchdog(loadStart);

            Transak.on(Transak.EVENTS.TRANSAK_WIDGET_INITIALISED, () => {
                this._clearStuckWatchdog();   // widget is alive — cancel the stuck signal
                this.refs.embedLoading.classList.add('hidden');
                this.refs.embedLoading.classList.remove('flex');
                window.dataLayer?.push({ event: 'transak_onramp_widget_open' });
            });

            Transak.on(Transak.EVENTS.TRANSAK_ORDER_CREATED, (order) => {
                window.dataLayer?.push({ event: 'transak_onramp_order_created', transak_order: order?.data });
            });
            Transak.on(Transak.EVENTS.TRANSAK_ORDER_SUCCESSFUL, (order) => {
                window.dataLayer?.push({ event: 'transak_onramp_order_successful', transak_order: order?.data });
            });
            Transak.on(Transak.EVENTS.TRANSAK_ORDER_FAILED, () => {
                window.dataLayer?.push({ event: 'transak_onramp_order_failed' });
            });
            Transak.on(Transak.EVENTS.TRANSAK_ORDER_CANCELLED, () => {
                window.dataLayer?.push({ event: 'transak_onramp_order_cancelled' });
            });
            Transak.on(Transak.EVENTS.TRANSAK_WIDGET_CLOSE, () => {
                window.dataLayer?.push({ event: 'transak_onramp_widget_close' });
                this._restoreCustomScreen();
            });

            this._transak.init();
        } catch (err) {
            console.error('[TransakOnramp] handoff error', err);
            this._clearStuckWatchdog();
            this._showError(err.message || 'Could not start checkout.');
            this.refs.cta.disabled = false;
            this._embedded = false;
        }
    }

    /**
     * Arm a timer that fires `transak_onramp_widget_stuck` if the embedded widget
     * never reaches TRANSAK_WIDGET_INITIALISED within STUCK_TIMEOUT_MS (Transak
     * refusing to serve / silent iframe failure). On fire we also hide the spinner
     * so it never permanently blocks the embed.
     *
     * @param {number|null} loadStart - performance.now() at launch, for elapsed_ms.
     */
    _startStuckWatchdog(loadStart) {
        this._clearStuckWatchdog();
        this._stuckTimer = setTimeout(() => {
            this._stuckTimer = null;
            this.refs.embedLoading.classList.add('hidden');
            this.refs.embedLoading.classList.remove('flex');
            window.dataLayer?.push({
                event:      'transak_onramp_widget_stuck',
                elapsed_ms: loadStart !== null ? Math.round(performance.now() - loadStart) : null,
                country:    this.buyCountry,
                page_path:  window.location.pathname,
            });
        }, STUCK_TIMEOUT_MS);
    }

    _clearStuckWatchdog() {
        if (this._stuckTimer !== null) {
            clearTimeout(this._stuckTimer);
            this._stuckTimer = null;
        }
    }

    /**
     * Restore the custom first screen after the embedded Transak widget closes.
     * Triggered by the TRANSAK_WIDGET_CLOSE event so users who back out / close
     * the widget land back on our screen instead of an empty iframe.
     */
    _restoreCustomScreen() {
        if (!this._embedded) return;

        this._clearStuckWatchdog();

        // Tear down the Transak instance and clear its DOM so a second handoff
        // starts cleanly. The SDK doesn't expose a destroy(); emptying the host
        // container is the documented way to remove the iframe.
        try { this.refs.embed.innerHTML = ''; } catch (_) {}
        this._transak = null;

        this.refs.embed.classList.add('hidden');
        this.refs.embedLoading.classList.add('hidden');
        this.refs.embedLoading.classList.remove('flex');
        this.refs.screen.classList.remove('hidden');

        this._embedded = false;
        this.refs.cta.disabled = false;

        // Quote may be stale after the user spent time inside the widget — refresh.
        this._scheduleQuote(0);
    }

    // -------------------------------------------------------------------------
    // UI helpers
    // -------------------------------------------------------------------------

    _showError(message) {
        if (!this.refs.error) return;
        this.refs.error.textContent = message;
        this.refs.error.classList.remove('hidden');
    }

    _hideError() {
        if (!this.refs.error) return;
        this.refs.error.classList.add('hidden');
        this.refs.error.textContent = '';
    }
}
